import { inject, Injectable } from '@angular/core';
import { HttpRequest, HttpEvent, HttpHandlerFn } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';
import { shareReplay, switchMap, take, map } from 'rxjs/operators';
import { AppKey, AppUrlService, AppSettingsService } from '@capital-access/common/settings';
import { Log } from '@capital-access/common/logging';
import { getUser } from '../..';
import { selectTargetUserId } from './+state/impersonation.selectors';
import { IMPERSONATION_CONSTANTS } from './impersonation.constants';
import { ImpersonationAuditActionType, IMPERSONATION_REQUEST_METADATA } from './models/impersonation-action-log.model';

const HTTP_METHOD_TO_ACTION_TYPE: Record<string, ImpersonationAuditActionType> = {
  POST: ImpersonationAuditActionType.CREATE,
  PUT: ImpersonationAuditActionType.UPDATE,
  PATCH: ImpersonationAuditActionType.UPDATE,
  DELETE: ImpersonationAuditActionType.DELETE
};

const EXCLUDED_AUDIT_URL_PATTERNS = ['/search', '/onesearch', '/logging'];

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private readonly appUrlService = inject(AppUrlService);
  private readonly settingsService = inject(AppSettingsService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly store = inject(Store);

  private readonly authEndpoints$: Observable<Set<string>> = this.oidcSecurityService.getConfiguration().pipe(
    take(1),
    map(config => {
      const endpoints = new Set<string>();
      if (config.authWellknownEndpoints) {
        Object.values(config.authWellknownEndpoints).forEach(endpoint => {
          if (endpoint) endpoints.add(endpoint);
        });
      }
      return endpoints;
    }),
    shareReplay(1)
  );

  navigateToCapitalAccessWithImpersonation(targetUserId: number): void {
    if (!targetUserId) {
      return;
    }

    const capitalAccessUrl = this.appUrlService.getAppUrl(AppKey.CAPITAL_ACCESS);

    if (capitalAccessUrl) {
      const url = new URL(capitalAccessUrl + 'user/my-account');
      url.searchParams.set(IMPERSONATION_CONSTANTS.TARGET_USER_ID_KEY, targetUserId.toString());
      url.searchParams.set(IMPERSONATION_CONSTANTS.IMPERSONATION_FLAG_KEY, 'true');

      window.location.href = url.toString();
    }
  }

  navigateBackToAdminPortal(): void {
    const adminUrl = this.appUrlService.getAppUrl(AppKey.ADMIN);
    if (adminUrl) {
      window.location.href = adminUrl + 'impersonation';
    }
  }

  clearLocalStorageData(): void {
    try {
      localStorage.removeItem(IMPERSONATION_CONSTANTS.TARGET_USER_ID_KEY);
      localStorage.removeItem(IMPERSONATION_CONSTANTS.IMPERSONATION_FLAG_KEY);
      localStorage.removeItem(IMPERSONATION_CONSTANTS.IMPERSONATION_HISTORY_ID);
    } catch (error) {
      Log.warn(`localStorage operation failed: ${error}`);
    }
  }

  isEventHubRequest(url: string): boolean {
    const { eventHubUrl, eventEmailsUrl } = this.getEventHubServiceUrls();
    return (!!eventHubUrl && url.startsWith(eventHubUrl)) || (!!eventEmailsUrl && url.startsWith(eventEmailsUrl));
  }

  isAuthEndpoint(url: string): Observable<boolean> {
    return this.authEndpoints$.pipe(map(endpoints => endpoints.has(url)));
  }

  processEventHubRequest(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    return this.store.select(getUser).pipe(
      take(1),
      switchMap(user => {
        if (!user?.samId) {
          return next(req);
        }
        return next(req.clone({ setHeaders: { 'X-Sam-Id': user.samId } }));
      })
    );
  }

  processStandardRequest(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    return this.store.select(selectTargetUserId).pipe(
      take(1),
      switchMap(targetUserId => {
        if (!targetUserId) {
          return next(req);
        }
        return next(req.clone({ setHeaders: { 'X-User-Id': targetUserId.toString() } }));
      })
    );
  }

  attachRequestMetadata<T>(req: HttpRequest<T>): HttpRequest<T> {
    if (!this.shouldLogAction(req.method, req.url)) {
      return req;
    }

    return req.clone({
      context: req.context.set(IMPERSONATION_REQUEST_METADATA, {
        method: req.method,
        url: req.url,
        body: req.body || {}
      })
    });
  }

  private shouldLogAction(method: string, url: string): boolean {
    const httpMethod = method.toUpperCase();

    if (!(httpMethod in HTTP_METHOD_TO_ACTION_TYPE)) {
      return false;
    }

    // Exclude POST requests to '/search', '/onesearch', and '/logging' endpoints from audit logging
    // because these endpoints are used for read-only operations and do not modify data.
    if (httpMethod === 'POST' && EXCLUDED_AUDIT_URL_PATTERNS.some(pattern => url.includes(pattern))) {
      return false;
    }

    return true;
  }

  private getEventHubServiceUrls(): { eventHubUrl: string | null; eventEmailsUrl: string | null } {
    const settings = this.settingsService.getSettings<{
      eventHubUrl?: string;
      httpClients?: { eventEmails?: { url?: string } };
    }>();

    return {
      eventHubUrl: settings.eventHubUrl || null,
      eventEmailsUrl: settings.httpClients?.eventEmails?.url || null
    };
  }
}
